/**
 * Formats a number according to Indian numbering system
 * @param {number} value - The number to format
 * @returns {string} - Formatted number with commas at crore/lacs/thousands
 */
export function formatIndianNumber(value) {
    if (value === null || value === undefined || isNaN(value)) {
        return '0';
    }

    const num = Math.abs(value);
    const numStr = Math.floor(num).toString();
    
    if (numStr.length <= 3) {
        return numStr;
    }
    
    // For Indian numbering system: first group of 3 digits from right, then groups of 2
    let result = '';
    let count = 0;
    
    for (let i = numStr.length - 1; i >= 0; i--) {
        if (count > 0 && ((count === 3) || (count > 3 && (count - 3) % 2 === 0))) {
            result = ',' + result;
        }
        result = numStr[i] + result;
        count++;
    }
    
    return result;
}

/**
 * Formats currency in Indian rupee format
 * @param {number} value - The currency value to format
 * @param {boolean} showDecimals - Whether to show decimal places (default: false)
 * @returns {string} - Formatted currency string
 */
export function formatIndianCurrency(value, showDecimals = false) {
    if (value === null || value === undefined || isNaN(value)) {
        return '₹0';
    }

    const isNegative = value < 0;
    const absValue = Math.abs(value);
    
    let formattedNumber;
    if (showDecimals) {
        const integerPart = Math.floor(absValue);
        const decimalPart = (absValue - integerPart).toFixed(2).substring(2);
        formattedNumber = formatIndianNumber(integerPart) + '.' + decimalPart;
    } else {
        formattedNumber = formatIndianNumber(Math.round(absValue));
    }
    
    const formattedValue = `₹${formattedNumber}`;
    return isNegative ? `-${formattedValue}` : formattedValue;
}

/**
 * Formats currency with 'k' suffix for thousands
 * @param {number} value - The currency value to format
 * @returns {string} - Formatted currency string with 'k' suffix if applicable
 */
export function formatIndianCurrencyCompact(value) {
    if (value === null || value === undefined || isNaN(value)) {
        return '₹0';
    }

    const isNegative = value < 0;
    const absValue = Math.abs(value);
    
    let displayValue = absValue;
    let suffix = '';
    
    if (absValue >= 1000) {
        displayValue = absValue / 1000;
        suffix = 'k';
    }
    
    const formattedNumber = formatIndianNumber(Math.round(displayValue));
    const formattedValue = `₹${formattedNumber}${suffix}`;
    return isNegative ? `-${formattedValue}` : formattedValue;
}

/**
 * Formats percentage values
 * @param {number} value - The percentage value to format
 * @param {boolean} signed - Whether to show + for positive values (default: true)
 * @returns {string} - Formatted percentage string
 */
export function formatPercentage(value, signed = true) {
    if (value === null || value === undefined || isNaN(value)) {
        return '0%';
    }

    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const formattedValue = `${Math.round(absValue * 10) / 10}%`;
    
    if (signed) {
        return isNegative ? `-${formattedValue}` : `+${formattedValue}`;
    } else {
        return formattedValue;
    }
}
